import React, {
    useState, useLayoutEffect, useMemo,
} from 'react';
import ReactDOM from 'react-dom';
import moment from 'moment';
import cls from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faTruck } from '@fortawesome/free-solid-svg-icons';
import { needsDarkText } from '@s20/common/util/style';
import { appendTimeZone } from '@s20/common/util/date';
import { statusColors } from './constants';

const JobCard = ({
    job, onClick, chipPosition = 'left', inPopover = false, currentView = 'month',
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const cardRef = React.useRef(null);
    const [weekViewLabelPos, setWeekViewLabelPos] = useState(null);
    const [popoverChipPos, setPopoverChipPos] = useState(null);


    // Extract the data we need
    const customerName = job.customerName || job.resource?.customerName || '';
    const streetAddress = job.streetAddress || job.resource?.streetAddress || '';

    // Use API counts if available, otherwise count from assignments array
    const assignments = job.assignments || job.resource?.assignments || [];
    const staffAssignments = assignments.filter((a) => a.employeeId);
    const vehicleAssignments = assignments.filter((a) => a.equipmentId);

    // Use the API-provided counts (capital case from C# DTO), falling back to counting assignments
    const assignedStaffCount = job.staffCount ?? job.resource?.staffCount ?? staffAssignments.length;
    const assignedVehicleCount = job.vehicleCount ?? job.resource?.vehicleCount ?? vehicleAssignments.length;

    // Get job type and color from API, fallback to gray
    const jobType = job.jobType || job.resource?.jobType || 'default';
    const jobTypeColor = job.jobTypeColor || job.resource?.jobTypeColor || '#9ca3af';

    // Determine job status
    const jobStatus = job.jobStatus || job.resource?.jobStatus || job.status || job.resource?.status || 'Unfilled';
    const statusColor = statusColors[jobStatus] || '#777';
    const isCancelStatus = jobStatus === 'Cancel Pending' || jobStatus === 'Cancel Confirmed';

    // Determine if job should have past background based on view type
    const now = moment();
    // Use job.date field (which is the correct calendar day) to determine if past
    // This works correctly with timezone-adjusted dates
    const jobDate = moment(job.date || job.resource?.date);
    let shouldShowPastBackground = false;

    if (currentView === 'month') {
        // Month view: compare against start of today
        shouldShowPastBackground = jobDate.isBefore(moment().startOf('day'));
    } else {
        // Week view: compare against current time
        // For week view, also check the time component
        const jobStart = moment(job.startTime || job.resource?.startTime || job.start);
        shouldShowPastBackground = jobStart.isBefore(now);
    }

    useLayoutEffect(() => {
        if (isHovered && (currentView === 'week' || currentView === 'day') && !inPopover && cardRef.current) {
            const rect = cardRef.current.getBoundingClientRect();
            setWeekViewLabelPos({
                top: rect.top + rect.height / 2,
                left: chipPosition === 'left' ? rect.left : rect.right,
            });
        } else if (isHovered && inPopover && cardRef.current) {
            const rect = cardRef.current.getBoundingClientRect();
            const chipLeft = chipPosition === 'left' ? rect.left + 3 : rect.right + 3;
            setPopoverChipPos({
                top: rect.top + rect.height / 2,
                left: chipPosition === 'left' ? 'auto' : chipLeft,
                right: chipPosition === 'left' ? window.innerWidth - rect.left - 3 : 'auto',
            });
        }
    }, [isHovered, inPopover, chipPosition, currentView]);

    const handleKeyDown = (e) => {
        if ((e.key === 'Enter' || e.key === ' ') && onClick) {
            e.preventDefault();
            onClick(job);
        }
    };

    // Build CSS classes
    const containerClasses = cls('job-card-container', {
        'job-card-container--past': shouldShowPastBackground,
        'in-popover': inPopover,
    });

    const contentClasses = cls('job-card-content', {
        'job-card-content--hovered': isHovered,
    });

    const statsClasses = cls('job-stats', {
        'job-stats--visible': isHovered,
    });

    // Memoize the job type chip rendering logic
    const jobTypeChip = useMemo(() => {
        if (jobType === 'default') return null;

        const chipColor = needsDarkText(jobTypeColor) ? '#000000' : '#ffffff';

        if (inPopover && isHovered) {
            return ReactDOM.createPortal(
                <div
                    className="job-type-chip job-type-chip--fixed"
                    style={{
                        position: 'fixed',
                        top: popoverChipPos ? `${popoverChipPos.top}px` : '50%',
                        left: popoverChipPos?.left !== 'auto' ? popoverChipPos?.left : 'auto',
                        right: popoverChipPos?.right !== 'auto' ? popoverChipPos?.right : 'auto',
                        transform: 'translateY(-50%)',
                        '--job-type-color': jobTypeColor,
                        '--chip-color': chipColor,
                    }}
                >
                    {jobType}
                </div>,
                document.body,
            );
        }

        if (!inPopover && (currentView === 'week' || currentView === 'day') && isHovered && weekViewLabelPos) {
            const chipClasses = cls('job-type-chip', 'job-type-chip--fixed', {
                'job-type-chip--week-left': chipPosition === 'left',
                'job-type-chip--week-right': chipPosition !== 'left',
            });

            return ReactDOM.createPortal(
                <div
                    className={chipClasses}
                    style={{
                        '--chip-top': `${weekViewLabelPos.top}px`,
                        '--chip-left': `${weekViewLabelPos.left}px`,
                        '--job-type-color': jobTypeColor,
                        '--chip-color': chipColor,
                    }}
                >
                    {jobType}
                </div>,
                document.body,
            );
        }

        if (!inPopover && currentView === 'month') {
            const chipClasses = cls('job-type-chip', {
                'job-type-chip--left': chipPosition === 'left',
                'job-type-chip--right': chipPosition !== 'left',
                'job-type-chip--visible': isHovered,
            });

            return (
                <div
                    className={chipClasses}
                    style={{
                        '--job-type-color': jobTypeColor,
                        '--chip-color': chipColor,
                    }}
                >
                    {jobType}
                </div>
            );
        }

        return null;
    }, [jobType, inPopover, isHovered, currentView, weekViewLabelPos, popoverChipPos, chipPosition, jobTypeColor]);

    return (
        <div
            ref={cardRef}
            role="button"
            tabIndex={onClick ? 0 : -1}
            aria-label={`Job ${job.jobNumber || job.id} for ${job.customerName || 'customer'} at ${appendTimeZone(moment.parseZone(job.startTime).format('h:mm A'), job.startTime, job.timeZone)}. Status: ${job.status}. Staff: ${job.staffCount} assigned of ${job.requestedStaffCount} requested. Vehicles: ${job.vehicleCount} assigned of ${job.requestedVehicleCount} requested.`}
            className={containerClasses}
            style={{
                '--job-type-color': jobTypeColor,
                '--status-color': statusColor,
            }}
            onClick={() => onClick && onClick(job)}
            onKeyDown={handleKeyDown}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Job Type Chip - appears on hover */}
            {jobTypeChip}

            {/* Card Content Container */}
            <div className={contentClasses}>
                {/* Customer Name */}
                <div className="job-card-customer-name">
                    {customerName}
                </div>

                {/* Street Address */}
                <div className="job-card-address">
                    {streetAddress}
                </div>

                {/* Status indicator - shows when not hovered */}
                {!isHovered && (
                    <div className="job-card-status-indicator">
                        {isCancelStatus ? (
                            // X for cancel statuses
                            <span className="job-card-status-indicator__x">
                                ✕
                            </span>
                        ) : (
                            // Dot for other statuses
                            <div className="job-card-status-indicator__dot" />
                        )}
                    </div>
                )}

                {/* Staff and Vehicle counts - appear on hover */}
                <div className={statsClasses}>
                    <div className="job-stats__item">
                        <FontAwesomeIcon icon={faUsers} />
                        {assignedStaffCount}
                    </div>
                    <div className="job-stats__item">
                        <FontAwesomeIcon icon={faTruck} />
                        {assignedVehicleCount}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default JobCard;
